import React, { useState } from 'react';
import { Typography, Descriptions, Tag, Button, Modal, Table, Collapse } from 'antd';
import { InfoCircleOutlined, EyeOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const TemplatePreview = ({ templates }) => {
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);

    const getFieldTypeColor = (type) => {
        const colors = {
            string: 'blue',
            number: 'green',
            date: 'orange',
            boolean: 'purple',
        };
        return colors[type] || 'default';
    };

    const getRequiredColor = (required) => {
        return required ? 'red' : 'green';
    };

    const getRequiredText = (required) => {
        return required ? 'Required' : 'Optional';
    };

    const handlePreviewTemplate = (templateKey) => {
        const template = templates[templateKey];
        setPreviewTemplate(template);
        setPreviewModalVisible(true);
    };

    return (
        <div>
            <Title level={4}>Available Data Templates</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                Review the available data templates below to understand what fields are required for each type of file.
                This will help you prepare your data file with the correct columns.
            </Text>

            <Collapse defaultActiveKey={[]}>
                {Object.entries(templates).map(([templateKey, template]) => (
                    <Panel
                        key={templateKey}
                        header={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <InfoCircleOutlined />
                                <Text strong>{template.name}</Text>
                                <Tag color="blue">{Object.keys(template.fields).length} fields</Tag>
                                <Tag color="red">
                                    {Object.values(template.fields).filter(field => field.required).length} required
                                </Tag>
                            </div>
                        }
                        extra={
                            <Button
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePreviewTemplate(templateKey);
                                }}
                            >
                                Detailed View
                            </Button>
                        }
                    >
                        <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="Description">
                                {template.description}
                            </Descriptions.Item>
                            <Descriptions.Item label="Filename Template">
                                <Text code>{template.filename_template}</Text>
                            </Descriptions.Item>
                        </Descriptions>

                        <Title level={5}>Required Fields:</Title>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                            {Object.entries(template.fields)
                                .filter(([_, field]) => field.required)
                                .map(([fieldKey, field]) => (
                                    <div key={fieldKey} style={{ marginBottom: 8 }}>
                                        <Tag color={getRequiredColor(field.required)}>
                                            {getRequiredText(field.required)}
                                        </Tag>
                                        <Tag color={getFieldTypeColor(field.type)}>
                                            {field.type}
                                        </Tag>
                                        <Text strong>{field.name}</Text>
                                        <br />
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {field.description}
                                        </Text>
                                    </div>
                                ))}
                        </div>

                        <Title level={5}>Optional Fields:</Title>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {Object.entries(template.fields)
                                .filter(([_, field]) => !field.required)
                                .map(([fieldKey, field]) => (
                                    <div key={fieldKey} style={{ marginBottom: 8 }}>
                                        <Tag color={getRequiredColor(field.required)}>
                                            {getRequiredText(field.required)}
                                        </Tag>
                                        <Tag color={getFieldTypeColor(field.type)}>
                                            {field.type}
                                        </Tag>
                                        <Text strong>{field.name}</Text>
                                        <br />
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {field.description}
                                        </Text>
                                    </div>
                                ))}
                        </div>
                    </Panel>
                ))}
            </Collapse>

            <Modal
                title={`Detailed Template View: ${previewTemplate?.name || ''}`}
                open={previewModalVisible}
                onCancel={() => setPreviewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setPreviewModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={900}
            >
                {previewTemplate && (
                    <div>
                        <Descriptions column={1} style={{ marginBottom: 24 }}>
                            <Descriptions.Item label="Name">
                                <Text strong>{previewTemplate.name}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Description">
                                {previewTemplate.description}
                            </Descriptions.Item>
                            <Descriptions.Item label="Filename Template">
                                <Text code>{previewTemplate.filename_template}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Total Fields">
                                {Object.keys(previewTemplate.fields).length}
                            </Descriptions.Item>
                            <Descriptions.Item label="Required Fields">
                                {Object.values(previewTemplate.fields).filter(field => field.required).length}
                            </Descriptions.Item>
                        </Descriptions>

                        <Title level={5}>All Fields</Title>
                        <Table
                            dataSource={Object.entries(previewTemplate.fields).map(([key, field]) => ({
                                key,
                                fieldKey: key,
                                name: field.name,
                                description: field.description,
                                type: field.type,
                                required: field.required
                            }))}
                            columns={[
                                {
                                    title: 'Field Name',
                                    dataIndex: 'name',
                                    key: 'name',
                                    render: (text, record) => (
                                        <div>
                                            <Text strong>{text}</Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {record.fieldKey}
                                            </Text>
                                        </div>
                                    )
                                },
                                {
                                    title: 'Description',
                                    dataIndex: 'description',
                                    key: 'description',
                                    ellipsis: true
                                },
                                {
                                    title: 'Type',
                                    dataIndex: 'type',
                                    key: 'type',
                                    render: (type) => (
                                        <Tag color={getFieldTypeColor(type)}>
                                            {type}
                                        </Tag>
                                    )
                                },
                                {
                                    title: 'Required',
                                    dataIndex: 'required',
                                    key: 'required',
                                    render: (required) => (
                                        <Tag color={getRequiredColor(required)}>
                                            {getRequiredText(required)}
                                        </Tag>
                                    )
                                }
                            ]}
                            pagination={false}
                            size="small"
                        />
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default TemplatePreview;
